import React from "react";
import { colors, primaryGradient } from "../../../theme/colors";
import {
  Box,
  Typography,
  InputAdornment,
  Chip,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import CodeIcon from "@mui/icons-material/Code";
import StorageIcon from "@mui/icons-material/Storage";
import DnsIcon from "@mui/icons-material/Dns";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import BarChartIcon from "@mui/icons-material/BarChart";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";

import {
  SearchContainer,
  SearchInputWrapper,
  CustomTextField,
  SearchButton,
  LoadingButton,
  HeaderSection,
  SearchIconWrapper,
  FeatureHighlight,
  PrimaryTextContainer,
  CustomAccordion,
} from "./style";
import { CustomStyledCircularProgress } from "../../../components/common-components/CustomStyledCircularProgress";

const SUGGESTED_ROLES = [
  { label: "Salesforce Developer", icon: <CloudOutlinedIcon fontSize="small" /> },
  { label: "Senior React Developer", icon: <CodeIcon fontSize="small" /> },
  { label: "Data Engineer", icon: <StorageIcon fontSize="small" /> },
  { label: "Backend Engineer", icon: <DnsIcon fontSize="small" /> },
  { label: "Node.js Developer", icon: <IntegrationInstructionsIcon fontSize="small" /> },
  { label: "Data Scientist", icon: <BarChartIcon fontSize="small" /> },
  { label: "DevOps Engineer", icon: <ViewInArIcon fontSize="small" /> },
  { label: "UI/UX Designer", icon: <DesktopWindowsIcon fontSize="small" /> },
];

const GlobalTalentSearchQuery = ({
  handleSearchGlobalTalentQuery,
  query,
  setQuery,
  isGlobalSearchingQuery,
  inputRef,
  handleKeyPress,
  accordionExpanded,
  setAccordionExpanded,
  searchSuggestions,
  handleSuggestionClick,
}) => {
  return (
    <SearchContainer>
      <HeaderSection>
        <PrimaryTextContainer>
          <SearchIconWrapper>
            <TravelExploreIcon />
          </SearchIconWrapper>
          <Typography className="main-title">Global Talent Search</Typography>
        </PrimaryTextContainer>

        <Typography className="subtitle">
          Find and hire the right talent, anywhere in the world
        </Typography>

        <FeatureHighlight>
          <AutoAwesomeIcon className="feature-icon" />
          <Typography variant="body2" className="feature-text">
            Powered by advanced AI &bull; Search across millions of profiles &bull; Real-time results
          </Typography>
        </FeatureHighlight>
      </HeaderSection>

      <SearchInputWrapper>
        <CustomTextField
          placeholder="Ask AI: e.g., 'Find a Senior React Developer in New York with 5+ years experience'"
          fullWidth
          value={query.query}
          onChange={(e) => setQuery({ ...query, query: e.target.value })}
          onKeyPress={handleKeyPress}
          disabled={isGlobalSearchingQuery}
          inputRef={inputRef}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className="search-icon" />
              </InputAdornment>
            ),
          }}
          variant="outlined"
        />

        {isGlobalSearchingQuery ? (
          <LoadingButton disabled>
            <span>AI Searching...</span>
            <CustomStyledCircularProgress
              className="cicular-progress"
              size={20}
            />
          </LoadingButton>
        ) : (
          <SearchButton
            variant="contained"
            onClick={handleSearchGlobalTalentQuery}
            disabled={!query.query?.trim()}
            startIcon={<AutoAwesomeIcon sx={{ fontSize: "18px !important" }} />}
            endIcon={<ChevronRightIcon sx={{ fontSize: "18px !important" }} />}
          >
            Search Talent
          </SearchButton>
        )}
      </SearchInputWrapper>

      {!query.query && (
        <Box
          sx={{
            mt: 3.5,
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            justifyContent: "center",
            maxWidth: "920px",
          }}
        >
          {SUGGESTED_ROLES.map((roleItem, index) => (
            <Chip
              key={index}
              icon={React.cloneElement(roleItem.icon, {
                sx: { color: `${colors.primary} !important`, fontSize: "18px !important" },
              })}
              label={roleItem.label}
              onClick={() => handleSuggestionClick(roleItem.label)}
              clickable
              sx={{
                backgroundColor: colors.primaryLight,
                borderColor: colors.primaryLightBorder,
                borderWidth: "1px",
                borderStyle: "solid",
                color: colors.primary,
                fontWeight: 600,
                fontSize: "0.875rem",
                height: "40px",
                px: 1.5,
                borderRadius: "30px",
                transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: "0 2px 6px rgba(127, 29, 29, 0.04)",
                "&:hover": {
                  backgroundColor: colors.sidebarSelected,
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 16px rgba(127, 29, 29, 0.12)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            />
          ))}
        </Box>
      )}

      {!query.query && (
        <CustomAccordion
          expanded={accordionExpanded}
          onChange={(event, isExpanded) => setAccordionExpanded(isExpanded)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon className="expandmore-icon" />}
            aria-controls="search-suggestions-content"
            id="search-suggestions-header"
          >
            <Typography variant="body1" className="expandmore-heading">
              <span role="img" aria-label="lightbulb">💡</span> More Frequently Search Skills
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box className="main-accordion-container">
              {searchSuggestions.map((suggestion, index) => (
                <Chip
                  key={index}
                  label={suggestion}
                  onClick={() => handleSuggestionClick(suggestion)}
                  clickable
                  size="medium"
                  sx={{
                    backgroundColor: colors.primaryLight,
                    borderColor: colors.primaryLightBorder,
                    borderWidth: "1px",
                    borderStyle: "solid",
                    color: colors.primary,
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    height: "36px",
                    borderRadius: "20px",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: `${colors.primary} !important`,
                      color: "#ffffff !important",
                      transform: "translateY(-1px)",
                    },
                  }}
                />
              ))}
            </Box>
          </AccordionDetails>
        </CustomAccordion>
      )}
    </SearchContainer>
  );
};

export default GlobalTalentSearchQuery;
